import {
  buildCaptureEmbedSnippet,
  channelSlug,
  checkFeedbackApiReady,
  createInstrument,
  getInstrument,
  listInstruments,
  listInstrumentsWithLimits,
  setInstrumentStatus,
  updateInstrument,
  upsertChannels,
} from "pages/VoiceCaptureManagement/feedbackService";
import { CHANNEL_TYPES, WIDGET_STATUS } from "pages/VoiceCaptureManagement/constants";
import config from "Configurations";
import { apiFileUpload, apiRequest } from "util/services";
import {
  BOT_STATUS,
  DEFAULT_BOT_DRAFT,
  DEFAULT_HANDOFF,
  INSTRUMENT_TYPE,
} from "./constants";

const STORAGE_KEY = "pivony.sonicProspect.bots.v1";
const FEEDBACK_BASE = "feedback";
const LATENCY_MS = 200;

const delay = (ms = LATENCY_MS) => new Promise((r) => setTimeout(r, ms));

function readLocal() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (_) {
    return [];
  }
}

function writeLocal(bots) {
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(bots));
}

function localId() {
  return `sp_local_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

let useLocalStore = false;

export function isUsingLocalStore() {
  return useLocalStore;
}


function normalizeCannedReplies(raw) {
  const list = Array.isArray(raw)
    ? raw
    : Array.isArray(DEFAULT_HANDOFF.cannedReplies)
    ? DEFAULT_HANDOFF.cannedReplies
    : [];
  const out = [];
  const seen = new Set();
  for (const item of list) {
    if (!item || typeof item !== "object" || out.length >= 10) break;
    const title = String(item.title || "").trim().slice(0, 80);
    const body = String(item.body || "").trim().slice(0, 1000);
    if (!title || !body) continue;
    let id = String(item.id || title)
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9_-]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 40);
    if (!id) id = `r${out.length + 1}`;
    if (seen.has(id)) id = `${id}-${out.length + 1}`;
    seen.add(id);
    out.push({ id, title, body });
  }
  return out;
}

function normalizeHandoff(raw) {
  const src = raw && typeof raw === "object" ? raw : {};
  const hasOwnReplies = Array.isArray(src.cannedReplies);
  return {
    enabled: !!src.enabled,
    availability: {
      enabled: src.availability?.enabled !== false,
      days: Array.isArray(src.availability?.days) && src.availability.days.length
        ? src.availability.days
        : [...DEFAULT_HANDOFF.availability.days],
      start: src.availability?.start || DEFAULT_HANDOFF.availability.start,
      end: src.availability?.end || DEFAULT_HANDOFF.availability.end,
      timezone: src.availability?.timezone || DEFAULT_HANDOFF.availability.timezone,
    },
    triggers: {
      highPurchaseIntent: src.triggers?.highPurchaseIntent !== false,
      pricingQuestion: src.triggers?.pricingQuestion !== false,
      leadCaptured: src.triggers?.leadCaptured !== false,
    },
    qualification: {
      requireLead: src.qualification?.requireLead === true,
      requireHighIntent: src.qualification?.requireHighIntent !== false,
    },
    routing: {
      destination: src.routing?.destination || DEFAULT_HANDOFF.routing.destination,
      teamLabel: src.routing?.teamLabel || DEFAULT_HANDOFF.routing.teamLabel,
      email: src.routing?.email || "",
    },
    cannedReplies: normalizeCannedReplies(
      hasOwnReplies ? src.cannedReplies : DEFAULT_HANDOFF.cannedReplies,
    ),
  };
}

export { checkFeedbackApiReady };

function mapConfigToBot(inst) {
  if (!inst) return null;
  const cfg = inst.config || {};
  const channels = inst.channels || [];
  return {
    id: inst.id,
    name: inst.title || inst.name || "",
    description: inst.description || "",
    status: inst.status || BOT_STATUS.DRAFT,
    createdAt: inst.created_at || inst.createdAt,
    updatedAt: inst.updated_at || inst.updatedAt,
    websiteUrl: cfg.website_url || "",
    language: cfg.language || "tr",
    greeting: cfg.greeting || "",
    goals: {
      product_qa: cfg.goals?.product_qa !== false,
      lead_capture: cfg.goals?.lead_capture !== false,
      handoff: !!cfg.goals?.handoff || !!cfg.handoff?.enabled,
    },
    handoff: normalizeHandoff(cfg.handoff),
    appearance: {
      brandColor: cfg.appearance?.brand_color || "#7c3aed",
      launcherPosition: cfg.appearance?.launcher_position || "bottom-right",
      hideWatermark: cfg.appearance?.hide_watermark === true,
    },
    knowledge: {
      faqItems: qnaItemsFromConfig(cfg),
      pdfDocuments: Array.isArray(cfg.knowledge?.pdf_documents)
        ? cfg.knowledge.pdf_documents
        : [],
      websites: Array.isArray(cfg.knowledge_sources?.websites)
        ? cfg.knowledge_sources.websites
        : [],
      systemPrompt: cfg.knowledge?.system_prompt || "",
      ragStatus: cfg.knowledge?.rag_status || null,
      ragUpdatedAt: cfg.knowledge?.rag_updated_at || null,
      ragError: cfg.knowledge?.rag_error || null,
    },
    knowledgeStats: inst.knowledge_stats || inst.knowledgeStats || null,
    embedLastSeenAt: cfg.embed_last_seen_at || inst.embed_last_seen_at || null,
    allowedDomains: Array.isArray(cfg.allowed_domains) ? cfg.allowed_domains : [],
    channels,
    enabledChannels: inst.enabled_channels || inst.enabledChannels || [],
    slug: channelSlug({ channels }, CHANNEL_TYPES.WEB_EMBED) || inst.slug || "",
    instagram: {
      connected: !!cfg.instagram?.connected,
      removed: !!cfg.instagram?.removed,
      igUserId: cfg.instagram?.ig_user_id || "",
      username: cfg.instagram?.username || "",
      pageId: cfg.instagram?.page_id || "",
    },
    whatsapp: {
      connected: !!cfg.whatsapp?.connected,
      removed: !!cfg.whatsapp?.removed,
      phoneNumberId: cfg.whatsapp?.phone_number_id || "",
      displayPhone: cfg.whatsapp?.display_phone || "",
      wabaId: cfg.whatsapp?.waba_id || "",
    },
  };
}

function qnaItemsFromConfig(cfg) {
  if (Array.isArray(cfg.custom_qna)) {
    return cfg.custom_qna.map((item) => ({
      q: item.question || item.q || "",
      a: item.answer || item.a || "",
    }));
  }
  return Array.isArray(cfg.knowledge?.faq_items) ? cfg.knowledge.faq_items : [];
}

function qnaItemsToPayload(faqItems) {
  return (faqItems || [])
    .filter((item) => (item.q || "").trim() && (item.a || "").trim())
    .map((item) => ({
      question: String(item.q).trim(),
      answer: String(item.a).trim(),
    }));
}

function botToPayload(bot) {
  const domainFromUrl = (bot.websiteUrl || "")
    .replace(/^https?:\/\//i, "")
    .split("/")[0]
    .trim();
  const allowed = bot.allowedDomains?.length
    ? bot.allowedDomains
    : domainFromUrl
    ? [domainFromUrl]
    : [];

  return {
    type: INSTRUMENT_TYPE,
    title: bot.name.trim(),
    description: bot.description || null,
    status: bot.status || BOT_STATUS.DRAFT,
    config: {
      website_url: bot.websiteUrl || "",
      language: bot.language || "tr",
      greeting: bot.greeting || "",
      goals: {
        ...(bot.goals || DEFAULT_BOT_DRAFT.goals),
        handoff: !!(bot.handoff?.enabled || bot.goals?.handoff),
      },
      handoff: normalizeHandoff(bot.handoff),
      appearance: {
        brand_color: bot.appearance?.brandColor || "#7c3aed",
        launcher_position: bot.appearance?.launcherPosition || "bottom-right",
        hide_watermark: bot.appearance?.hideWatermark === true,
      },
      custom_qna: qnaItemsToPayload(bot.knowledge?.faqItems),
      knowledge: {
        faq_items: bot.knowledge?.faqItems || [],
        pdf_documents: bot.knowledge?.pdfDocuments || [],
        system_prompt: bot.knowledge?.systemPrompt || "",
      },
      allowed_domains: allowed,
    },
  };
}

async function withFallback(apiFn, localFn) {
  if (useLocalStore) return localFn();
  try {
    return await apiFn();
  } catch (e) {
    if (e?.unavailable) {
      useLocalStore = true;
      return localFn();
    }
    throw e;
  }
}

export function prospectBotRagStatus(bot) {
  if (!bot) return null;
  return (
    bot.knowledgeStats?.rag_status ||
    bot.knowledgeStats?.ragStatus ||
    bot.knowledge?.ragStatus ||
    bot.knowledge?.rag_status ||
    null
  );
}

export async function listProspectBots() {
  return withFallback(
    async () => {
      const { items, prospect_limits: limits } = await listInstrumentsWithLimits(
        INSTRUMENT_TYPE,
      );
      return {
        bots: items.map(mapConfigToBot),
        limits,
      };
    },
    async () => {
      await delay();
      const list = readLocal().sort(
        (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt),
      );
      return {
        bots: list,
        limits: {
          tier: "standard",
          max_pages: 200,
          bot_limit: 1,
          bot_count: list.length,
          can_create_bot: true,
        },
      };
    },
  );
}

export async function getProspectBot(id) {
  return withFallback(
    async () => mapConfigToBot(await getInstrument(id)),
    async () => {
      await delay(80);
      const found = readLocal().find((b) => String(b.id) === String(id));
      return found ? { ...found } : null;
    },
  );
}

export async function createProspectBot(bot) {
  const payload = botToPayload({ ...DEFAULT_BOT_DRAFT, ...bot });
  return withFallback(
    async () => mapConfigToBot(await createInstrument(payload)),
    async () => {
      await delay();
      const now = new Date().toISOString();
      const record = {
        ...mapConfigToBot({
          id: localId(),
          title: payload.title,
          description: payload.description,
          status: payload.status,
          created_at: now,
          updated_at: now,
          config: payload.config,
          channels: [],
        }),
      };
      const bots = readLocal();
      bots.unshift(record);
      writeLocal(bots);
      return record;
    },
  );
}

export async function updateProspectBot(id, bot) {
  const payload = botToPayload(bot);
  return withFallback(
    async () => mapConfigToBot(await updateInstrument(id, payload)),
    async () => {
      await delay();
      const bots = readLocal();
      const idx = bots.findIndex((b) => String(b.id) === String(id));
      if (idx < 0) throw new Error("NOT_FOUND");
      const updated = {
        ...bots[idx],
        ...mapConfigToBot({
          id,
          title: payload.title,
          description: payload.description,
          status: payload.status,
          updated_at: new Date().toISOString(),
          config: payload.config,
          channels: bots[idx].channels || [],
        }),
      };
      bots[idx] = updated;
      writeLocal(bots);
      return updated;
    },
  );
}

export async function deleteProspectBot(id) {
  return withFallback(
    async () => {
      const res = await apiRequest(
        `${FEEDBACK_BASE}/instruments/${id}`,
        "DELETE",
        null,
        true,
      );
      if (res?.error || res?.error_code) {
        throw new Error(res?.error || res?.error_code || "DELETE_FAILED");
      }
      return {
        success: true,
        vectorPurge: res?.vector_purge
          ? {
              scheduled: !!res.vector_purge.scheduled,
              purge_after: res.vector_purge.purge_after,
              retention_days: res.vector_purge.retention_days,
            }
          : null,
      };
    },
    async () => {
      await delay();
      writeLocal(readLocal().filter((b) => String(b.id) !== String(id)));
      return { success: true, vectorPurge: null };
    },
  );
}

export async function connectProspectInstagram(id, { igUserId, pageId, accessToken }) {
  const res = await apiRequest(
    `${FEEDBACK_BASE}/instruments/${id}/instagram/connect`,
    "POST",
    {
      ig_user_id: igUserId,
      page_id: pageId,
      access_token: accessToken,
    },
    true,
  );
  if (res?.error || !res?.instrument) {
    const err = new Error(res?.error || "INSTAGRAM_CONNECT_FAILED");
    err.code = res?.error;
    throw err;
  }
  return mapConfigToBot(res.instrument);
}

export async function listProspectMemberships() {
  const res = await apiRequest(`${FEEDBACK_BASE}/prospect/memberships`, "GET", null, true);
  if (res?.error) {
    const err = new Error(res.error);
    err.code = res.error;
    throw err;
  }
  return res || { pricing: null, memberships: [] };
}

export async function acceptProspectMembership(productKey) {
  const res = await apiRequest(
    `${FEEDBACK_BASE}/prospect/memberships/${productKey}/accept`,
    "POST",
    {},
    true,
  );
  if (res?.error || !res?.membership) {
    const err = new Error(res?.error || "MEMBERSHIP_ACCEPT_FAILED");
    err.code = res?.error;
    throw err;
  }
  return res.membership;
}

export async function startProspectInstagramOAuth(instrumentId) {
  const res = await apiRequest(
    `${FEEDBACK_BASE}/prospect/instagram/oauth/start?instrument_id=${instrumentId}`,
    "GET",
    null,
    true,
  );
  if (res?.error) {
    const err = new Error(res.error);
    err.code = res.error;
    throw err;
  }
  return res;
}

export async function connectProspectWhatsapp(id, { code, phoneNumberId, wabaId }) {
  const res = await apiRequest(
    `${FEEDBACK_BASE}/instruments/${id}/whatsapp/connect`,
    "POST",
    {
      code,
      phone_number_id: phoneNumberId,
      waba_id: wabaId,
    },
    true,
  );
  if (res?.error || !res?.instrument) {
    const err = new Error(res?.error || "WHATSAPP_CONNECT_FAILED");
    err.code = res?.error;
    throw err;
  }
  return mapConfigToBot(res.instrument);
}

export async function startProspectEtsyOAuth() {
  const res = await apiRequest(
    `${FEEDBACK_BASE}/prospect/etsy/oauth/start`,
    "GET",
    null,
    true,
  );
  if (res?.error) {
    const err = new Error(res.error);
    err.code = res.error;
    throw err;
  }
  return res;
}

export async function testProspectEtsy(message) {
  const res = await apiRequest(
    `${FEEDBACK_BASE}/prospect/etsy/test`,
    "POST",
    { message },
    true,
  );
  if (res?.error) {
    const err = new Error(res.error);
    err.code = res.error;
    throw err;
  }
  return res;
}

export async function disconnectProspectWhatsapp(id) {
  const res = await apiRequest(
    `${FEEDBACK_BASE}/instruments/${id}/whatsapp/disconnect`,
    "POST",
    {},
    true,
  );
  if (res?.error || !res?.instrument) {
    throw new Error(res?.error || "WHATSAPP_DISCONNECT_FAILED");
  }
  return mapConfigToBot(res.instrument);
}

export async function disconnectProspectInstagram(id) {
  const res = await apiRequest(
    `${FEEDBACK_BASE}/instruments/${id}/instagram/disconnect`,
    "POST",
    {},
    true,
  );
  if (res?.error || !res?.instrument) {
    throw new Error(res?.error || "INSTAGRAM_DISCONNECT_FAILED");
  }
  return mapConfigToBot(res.instrument);
}

export async function setProspectBotStatus(id, status) {
  return withFallback(
    async () => mapConfigToBot(await setInstrumentStatus(id, status)),
    async () => {
      await delay();
      const bots = readLocal();
      const idx = bots.findIndex((b) => String(b.id) === String(id));
      if (idx < 0) throw new Error("NOT_FOUND");
      bots[idx] = {
        ...bots[idx],
        status,
        updatedAt: new Date().toISOString(),
      };
      writeLocal(bots);
      return bots[idx];
    },
  );
}

export async function enableProspectEmbed(id, bot, displayMode = "popup") {
  const launcher = bot.appearance?.launcherPosition || "bottom-right";
  return withFallback(
    async () => {
      const updated = await upsertChannels(id, [
        {
          channel_type: CHANNEL_TYPES.WEB_EMBED,
          enabled: true,
          config: {
            display_mode: displayMode,
            launcher_position: launcher,
            slug: bot.slug || undefined,
          },
        },
      ]);
      return mapConfigToBot(updated);
    },
    async () => {
      await delay();
      const bots = readLocal();
      const idx = bots.findIndex((b) => String(b.id) === String(id));
      if (idx < 0) throw new Error("NOT_FOUND");
      const slug = /^sp_[a-z0-9]{6,}$/.test(bots[idx].slug || "")
        ? bots[idx].slug
        : opaqueBotId();
      bots[idx] = {
        ...bots[idx],
        slug,
        channels: [
          {
            channel_type: CHANNEL_TYPES.WEB_EMBED,
            enabled: true,
            slug,
            config: { display_mode: displayMode, launcher_position: launcher },
          },
        ],
        updatedAt: new Date().toISOString(),
      };
      writeLocal(bots);
      return bots[idx];
    },
  );
}

function opaqueBotId() {
  const alphabet = "abcdefghijkmnopqrstuvwxyz23456789";
  let body = "";
  for (let i = 0; i < 8; i += 1) {
    body += alphabet[Math.floor(Math.random() * alphabet.length)];
  }
  return `sp_${body}`;
}

export function buildProspectEmbedSnippet(bot) {
  const slug = bot?.slug;
  if (!slug) return "";
  const launcher = bot.appearance?.launcherPosition || "bottom-right";
  const cdn =
    process.env.REACT_APP_SONIC_PROSPECT_CDN ||
    (typeof window !== "undefined" &&
    window.location.origin.includes("demo.pivony.com")
      ? `${window.location.origin}/sonic-prospect/v1/loader.js`
      : "https://cdn.pivony.com/sonic-prospect/v1/loader.js");
  const apiBase = (config.BASE_URL || "").replace(/\/+$/, "");
  const needsApi =
    apiBase &&
    !apiBase.includes("api.pivony.com") &&
    !apiBase.includes("demoapi.pivony.com");
  return (
    `<script src="${cdn}"\n` +
    `  data-bot-id="${slug}"\n` +
    `  data-launcher="${launcher}"` +
    (needsApi ? `\n  data-api="${apiBase}"` : "") +
    `\n  async></script>`
  );
}

export function computeChecklist(bot) {
  const summary = summarizeKnowledgeSources(bot);
  const hasKnowledge =
    summary.faqCount >= 1 ||
    summary.pdfCount >= 1 ||
    summary.pagesIndexed > 0 ||
    summary.crawlStatus === "processing";
  const hasName = Boolean((bot?.name || "").trim());
  const hasBrand = Boolean(bot?.appearance?.brandColor);
  const hasEmbed = Boolean(bot?.slug) && bot?.status === BOT_STATUS.ACTIVE;
  return {
    named: hasName,
    knowledge: hasKnowledge,
    brand: hasBrand,
    embed: hasEmbed,
  };
}

/** Breakdown for bot list chips — includes website crawl, not just PDF/FAQ. */
export function summarizeKnowledgeSources(bot) {
  const faqCount = (bot?.knowledge?.faqItems || []).filter(
    (item) => (item.q || "").trim() && (item.a || "").trim(),
  ).length;
  const pdfCount = (bot?.knowledge?.pdfDocuments || []).length;
  const websites = (bot?.knowledge?.websites || []).filter(Boolean);

  let pagesIndexed = 0;
  let crawlStatus = "none";
  let processingSite = null;

  for (const site of websites) {
    const status = site?.status;
    if (status === "processing") {
      crawlStatus = "processing";
      processingSite = site;
    } else if (status === "completed") {
      pagesIndexed += Number(site.pages_unique ?? site.pages_crawled ?? 0) || 0;
      if (crawlStatus !== "processing") {
        crawlStatus = "completed";
      }
    } else if (status === "failed" && crawlStatus === "none") {
      crawlStatus = "failed";
    }
  }

  if (websites.length === 0) {
    crawlStatus = "none";
  }

  return {
    faqCount,
    pdfCount,
    pagesIndexed,
    crawlStatus,
    processingSite,
    hasAnySource: faqCount + pdfCount + pagesIndexed > 0 || crawlStatus === "processing",
  };
}

export async function uploadKnowledgeDocument(botId, file) {
  return withFallback(
    async () => {
      const res = await apiFileUpload(
        `${FEEDBACK_BASE}/instruments/${botId}/knowledge-documents`,
        file,
      );
      if (!res?.document || !res?.instrument) {
        if (res?.error_code === "DUPLICATE_DOCUMENT" || res?.error === "DUPLICATE_DOCUMENT") {
          throw new Error("DUPLICATE_DOCUMENT");
        }
        throw new Error(res?.error || "UPLOAD_FAILED");
      }
      return {
        document: res.document,
        bot: mapConfigToBot(res.instrument),
      };
    },
    async () => {
      await delay();
      const bots = readLocal();
      const idx = bots.findIndex((b) => String(b.id) === String(botId));
      if (idx < 0) throw new Error("NOT_FOUND");
      const existing = bots[idx].knowledge?.pdfDocuments || [];
      const nameKey = (file.name || "").trim().toLowerCase();
      if (existing.some((d) => (d.name || "").trim().toLowerCase() === nameKey)) {
        throw new Error("DUPLICATE_DOCUMENT");
      }
      const document = {
        id: `pdf_local_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        name: file.name,
        size: file.size,
        url: "",
        uploaded_at: new Date().toISOString(),
      };
      const pdfDocuments = [...(bots[idx].knowledge?.pdfDocuments || []), document];
      bots[idx] = {
        ...bots[idx],
        knowledge: { ...bots[idx].knowledge, pdfDocuments },
        updatedAt: new Date().toISOString(),
      };
      writeLocal(bots);
      return { document, bot: bots[idx] };
    },
  );
}

export async function deleteKnowledgeDocument(botId, docId) {
  return withFallback(
    async () => {
      const res = await apiRequest(
        `${FEEDBACK_BASE}/instruments/${botId}/knowledge-documents/${docId}`,
        "DELETE",
        null,
        true,
      );
      if (!res?.instrument) {
        throw new Error(res?.error || "DELETE_FAILED");
      }
      return mapConfigToBot(res.instrument);
    },
    async () => {
      await delay();
      const bots = readLocal();
      const idx = bots.findIndex((b) => String(b.id) === String(botId));
      if (idx < 0) throw new Error("NOT_FOUND");
      const pdfDocuments = (bots[idx].knowledge?.pdfDocuments || []).filter(
        (d) => d.id !== docId,
      );
      bots[idx] = {
        ...bots[idx],
        knowledge: { ...bots[idx].knowledge, pdfDocuments },
        updatedAt: new Date().toISOString(),
      };
      writeLocal(bots);
      return bots[idx];
    },
  );
}

export async function startKnowledgeLinkCrawl(botId, url, language) {
  const res = await apiRequest(
    `${FEEDBACK_BASE}/instruments/${botId}/knowledge-links`,
    "POST",
    { url, language: language || undefined },
    true,
  );
  if (res?.cancel) throw new Error("CANCELLED");
  const code = res?.error_code || res?.error;
  if (code) throw new Error(code);
  if (!res?.website) throw new Error("CRAWL_FAILED");
  return res;
}

export async function getKnowledgeSources(botId) {
  const res = await apiRequest(
    `${FEEDBACK_BASE}/instruments/${botId}/knowledge-sources`,
    "GET",
    null,
    true,
  );
  if (res?.cancel) throw new Error("CANCELLED");
  if (res?.error || res?.error_code) {
    throw new Error(res.error_code || res.error);
  }
  return {
    websites: Array.isArray(res?.websites) ? res.websites : [],
    pdfDocuments: Array.isArray(res?.pdf_documents) ? res.pdf_documents : [],
  };
}

export async function listProspectSessions(botId) {
  const res = await apiRequest(
    `${FEEDBACK_BASE}/instruments/${botId}/prospect-sessions`,
    "GET",
    null,
    true,
  );
  if (res?.cancel) throw new Error("CANCELLED");
  if (res?.error || res?.error_code) {
    throw new Error(res.error_code || res.error);
  }
  return Array.isArray(res?.sessions) ? res.sessions : [];
}

export async function listProspectLeads(botId) {
  const res = await apiRequest(
    `${FEEDBACK_BASE}/instruments/${botId}/prospect-leads`,
    "GET",
    null,
    true,
  );
  if (res?.cancel) throw new Error("CANCELLED");
  if (res?.error || res?.error_code) {
    throw new Error(res.error_code || res.error);
  }
  return Array.isArray(res?.leads) ? res.leads : [];
}

export async function getProspectLeadCount(botId) {
  const res = await apiRequest(
    `${FEEDBACK_BASE}/instruments/${botId}/prospect-leads/count`,
    "GET",
    null,
    true,
  );
  if (res?.cancel) throw new Error("CANCELLED");
  if (res?.error || res?.error_code) {
    throw new Error(res.error_code || res.error);
  }
  const count = Number(res?.count);
  return Number.isFinite(count) && count >= 0 ? count : 0;
}

/** A3-C: Qualified Prospect audit list (no billing / Stripe). */
export async function listQualifiedProspects(botId, { limit = 20, offset = 0 } = {}) {
  const q = new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
  });
  const res = await apiRequest(
    `${FEEDBACK_BASE}/instruments/${botId}/qualified-prospects?${q}`,
    "GET",
    null,
    true,
  );
  if (res?.cancel) throw new Error("CANCELLED");
  if (res?.error || res?.error_code) {
    throw new Error(res.error_code || res.error);
  }
  const items = Array.isArray(res?.qualified_prospects)
    ? res.qualified_prospects
    : [];
  const count = Number(res?.count);
  const orgCount = Number(res?.org_count);
  return {
    items,
    count: Number.isFinite(count) && count >= 0 ? count : items.length,
    orgCount: Number.isFinite(orgCount) && orgCount >= 0 ? orgCount : null,
    pricing: res?.pricing && typeof res.pricing === "object" ? res.pricing : null,
    spendProtection:
      res?.spend_protection && typeof res.spend_protection === "object"
        ? res.spend_protection
        : null,
    limit: Number(res?.limit) || limit,
    offset: Number(res?.offset) || offset,
  };
}

/** L3: spend / QP monthly protection status. */
export async function getSpendProtection(botId) {
  const res = await apiRequest(
    `${FEEDBACK_BASE}/instruments/${botId}/spend-protection`,
    "GET",
    null,
    true,
  );
  if (res?.cancel) throw new Error("CANCELLED");
  if (res?.error || res?.error_code) {
    throw new Error(res.error_code || res.error);
  }
  return res?.spend_protection && typeof res.spend_protection === "object"
    ? res.spend_protection
    : null;
}

/** L3: update monthly QP / estimated spend caps (null = unlimited). */
export async function updateSpendProtection(botId, payload) {
  const res = await apiRequest(
    `${FEEDBACK_BASE}/instruments/${botId}/spend-protection`,
    "PUT",
    payload || {},
    true,
  );
  if (res?.cancel) throw new Error("CANCELLED");
  if (res?.error || res?.error_code) {
    throw new Error(res.error_code || res.error || res.message);
  }
  return res?.spend_protection && typeof res.spend_protection === "object"
    ? res.spend_protection
    : null;
}

/** A3-C: Qualified Prospect count only. */
export async function getQualifiedProspectCount(botId) {
  const res = await apiRequest(
    `${FEEDBACK_BASE}/instruments/${botId}/qualified-prospects/count`,
    "GET",
    null,
    true,
  );
  if (res?.cancel) throw new Error("CANCELLED");
  if (res?.error || res?.error_code) {
    throw new Error(res.error_code || res.error);
  }
  const count = Number(res?.count);
  return Number.isFinite(count) && count >= 0 ? count : 0;
}

/** Map API session row for conversations UI. */
export function mapProspectSessionRow(row) {
  const updatedAt = row.updated_at ? Date.parse(row.updated_at) : 0;
  const lead = row.lead || null;
  return {
    id: row.id,
    title: row.title || "",
    messageCount: row.message_count || 0,
    updatedAt: Number.isFinite(updatedAt) ? updatedAt : 0,
    ip: row.ip || "",
    rating: row.rating || "",
    channel: row.channel || "website",
    leadEmail: lead?.email || "",
    leadSegment: lead?.segment || "",
    leadCapturedAt: lead?.captured_at || "",
  };
}

/** Map API lead row (prospect-leads endpoint). */
export function mapProspectLeadRow(row) {
  const updatedAt = row.updated_at ? Date.parse(row.updated_at) : 0;
  const capturedAt = row.lead?.captured_at ? Date.parse(row.lead.captured_at) : 0;
  return {
    sessionId: row.session_id,
    title: row.title || "",
    messageCount: row.message_count || 0,
    updatedAt: Number.isFinite(updatedAt) ? updatedAt : 0,
    capturedAt: Number.isFinite(capturedAt) ? capturedAt : 0,
    ip: row.ip || "",
    rating: row.rating || "",
    lead: row.lead || null,
  };
}

/** Newest session timestamp, or null if the bot has never been chatted with. */
export function lastChatFromSessions(sessions) {
  let max = 0;
  (sessions || []).forEach((row) => {
    const ts = Date.parse(row?.updated_at || row?.created_at || "");
    if (Number.isFinite(ts) && ts > max) max = ts;
  });
  return max || null;
}

export async function getProspectSession(botId, sessionId) {
  const res = await apiRequest(
    `${FEEDBACK_BASE}/instruments/${botId}/prospect-sessions/${sessionId}`,
    "GET",
    null,
    true,
  );
  if (res?.cancel) throw new Error("CANCELLED");
  if (res?.error || res?.error_code) {
    throw new Error(res.error_code || res.error);
  }
  return res?.session || null;
}

export async function liveTakeover(botId, sessionId, body = {}) {
  const res = await apiRequest(
    `${FEEDBACK_BASE}/instruments/${botId}/prospect-sessions/${sessionId}/live-takeover`,
    "POST",
    body,
    true,
  );
  if (res?.cancel) throw new Error("CANCELLED");
  if (res?.error || res?.error_code) {
    throw new Error(res.error_code || res.error);
  }
  return res;
}

export async function liveAgentMessage(botId, sessionId, text, body = {}) {
  const res = await apiRequest(
    `${FEEDBACK_BASE}/instruments/${botId}/prospect-sessions/${sessionId}/live-message`,
    "POST",
    { message: text, ...body },
    true,
  );
  if (res?.cancel) throw new Error("CANCELLED");
  if (res?.error || res?.error_code) {
    throw new Error(res.error_code || res.error);
  }
  return res;
}

export async function liveEndHandoff(botId, sessionId, body = {}) {
  const res = await apiRequest(
    `${FEEDBACK_BASE}/instruments/${botId}/prospect-sessions/${sessionId}/live-end`,
    "POST",
    body,
    true,
  );
  if (res?.cancel) throw new Error("CANCELLED");
  if (res?.error || res?.error_code) {
    throw new Error(res.error_code || res.error);
  }
  return res;
}

export async function liveReleaseHandoff(botId, sessionId) {
  const res = await apiRequest(
    `${FEEDBACK_BASE}/instruments/${botId}/prospect-sessions/${sessionId}/live-release`,
    "POST",
    {},
    true,
  );
  if (res?.cancel) throw new Error("CANCELLED");
  if (res?.error || res?.error_code) {
    throw new Error(res.error_code || res.error);
  }
  return res;
}

export async function liveTransferHandoff(botId, sessionId, targetAgentId) {
  const res = await apiRequest(
    `${FEEDBACK_BASE}/instruments/${botId}/prospect-sessions/${sessionId}/live-transfer`,
    "POST",
    { target_agent_id: targetAgentId },
    true,
  );
  if (res?.cancel) throw new Error("CANCELLED");
  if (res?.error || res?.error_code) {
    throw new Error(res.error_code || res.error);
  }
  return res;
}

export async function listLiveAgents(botId) {
  const res = await apiRequest(
    `${FEEDBACK_BASE}/instruments/${botId}/live-agents`,
    "GET",
    null,
    true,
  );
  if (res?.cancel) throw new Error("CANCELLED");
  if (res?.error || res?.error_code) {
    throw new Error(res.error_code || res.error);
  }
  return Array.isArray(res?.agents) ? res.agents : [];
}

export async function liveTyping(botId, sessionId, typing) {
  const res = await apiRequest(
    `${FEEDBACK_BASE}/instruments/${botId}/prospect-sessions/${sessionId}/live-typing`,
    "POST",
    { typing: !!typing },
    true,
  );
  if (res?.cancel) throw new Error("CANCELLED");
  if (res?.error || res?.error_code) {
    throw new Error(res.error_code || res.error);
  }
  return res;
}

/**
 * Authenticated SSE for a live console thread (fetch stream; JWT header).
 * onEvent(name, data) — name is snapshot|ping|hello|bye|error
 * Auto-reconnects after server `bye` / clean end (proxy-safe ~50s windows).
 * Returns an abort function.
 */
export function openLiveSessionStream(botId, sessionId, { onEvent, onError } = {}) {
  let stopped = false;
  let abortCurrent = null;
  let failStreak = 0;

  const sleep = (ms) =>
    new Promise((resolve) => {
      window.setTimeout(resolve, ms);
    });

  const runOnce = async () => {
    const controller = new AbortController();
    abortCurrent = controller;
    const tokenStr = window.localStorage.getItem("jwt_token");
    const item = tokenStr ? JSON.parse(tokenStr) : null;
    const token = item?.value;
    if (!token || !botId || !sessionId) {
      throw new Error("MISSING_AUTH");
    }
    const base = process.env.REACT_APP_CUSTOM_API_URL || "";
    const url = `${String(base).replace(/\/$/, "")}/${FEEDBACK_BASE}/instruments/${botId}/prospect-sessions/${encodeURIComponent(sessionId)}/live-stream`;
    const res = await fetch(url, {
      method: "GET",
      headers: {
        Accept: "text/event-stream",
        Authorization: `Bearer ${token}`,
      },
      credentials: "omit",
      signal: controller.signal,
    });
    if (!res.ok || !res.body) {
      throw new Error(`STREAM_HTTP_${res.status}`);
    }
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let sawBye = false;
    while (!stopped) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const chunks = buffer.split("\n\n");
      buffer = chunks.pop() || "";
      for (const block of chunks) {
        const lines = block.split("\n");
        let eventName = "message";
        const dataLines = [];
        lines.forEach((line) => {
          if (line.startsWith("event:")) {
            eventName = line.slice(6).trim();
          } else if (line.startsWith("data:")) {
            dataLines.push(line.slice(5).trim());
          }
        });
        if (!dataLines.length) continue;
        let data = {};
        try {
          data = JSON.parse(dataLines.join("\n"));
        } catch (_) {
          data = { raw: dataLines.join("\n") };
        }
        if (eventName === "bye") sawBye = true;
        if (typeof onEvent === "function") onEvent(eventName, data);
      }
    }
    return sawBye;
  };

  const run = async () => {
    while (!stopped) {
      try {
        await runOnce();
        failStreak = 0;
        if (stopped) return;
        // Clean end / bye → soft reconnect (under proxy timeouts)
        await sleep(250);
      } catch (err) {
        if (stopped || err?.name === "AbortError") return;
        failStreak += 1;
        if (failStreak >= 2 && typeof onError === "function") {
          onError(err);
          return;
        }
        await sleep(800);
      }
    }
  };

  run();
  return () => {
    stopped = true;
    try {
      if (abortCurrent) abortCurrent.abort();
    } catch (_) {
      /* ignore */
    }
  };
}

/** Best-effort end on tab close (keepalive). */
export function liveEndHandoffBeacon(botId, sessionId, body = {}) {
  try {
    const tokenStr = window.localStorage.getItem("jwt_token");
    const item = tokenStr ? JSON.parse(tokenStr) : null;
    const token = item?.value;
    if (!token || !botId || !sessionId) return;
    // Same host prefix as apiRequest (Configurations.BASE_URL via relative path works when empty)
    const base = process.env.REACT_APP_CUSTOM_API_URL || "";
    const url = `${String(base).replace(/\/$/, "")}/${FEEDBACK_BASE}/instruments/${botId}/prospect-sessions/${encodeURIComponent(sessionId)}/live-end`;
    fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        system_text:
          body.system_text || "Sales left the chat. The assistant is back.",
        reason: body.reason || "agent_disconnect",
      }),
      keepalive: true,
      credentials: "omit",
    }).catch(() => {});
  } catch (_) {
    /* ignore */
  }
}

// --- HubSpot (one connection per account) ---------------------------------------

async function hubspotRequest(path, method = "GET", payload = null) {
  const res = await apiRequest(`${FEEDBACK_BASE}/prospect/crm/hubspot${path}`, method, payload, true);
  if (!res || res.error) {
    const err = new Error(res?.error || "HUBSPOT_REQUEST_FAILED");
    err.code = res?.error;
    throw err;
  }
  return res;
}

export const getProspectHubspot = () => hubspotRequest("");
export const startProspectHubspotOAuth = () => hubspotRequest("/oauth/start");
export const listProspectHubspotPipelines = () => hubspotRequest("/pipelines");
export const saveProspectHubspotSettings = (settings) => hubspotRequest("/settings", "PUT", settings);
export const disconnectProspectHubspot = () => hubspotRequest("", "DELETE");

// --- Conversations (fair use + extended conversations, one setting per account) ---

async function conversationSettingsRequest(method, payload = null) {
  const res = await apiRequest(`${FEEDBACK_BASE}/prospect/conversations/settings`, method, payload, true);
  if (!res || res.error || res.cancel) {
    const err = new Error(res?.message || res?.error || "CONVERSATION_SETTINGS_FAILED");
    err.code = res?.error;
    err.apiMessage = res?.message || "";
    throw err;
  }
  return res;
}

export const getProspectConversationSettings = () => conversationSettingsRequest("GET");
export const saveProspectConversationSettings = (body) => conversationSettingsRequest("PUT", body);

// --- Example demo links (Pivony staff only: which links are opened and used) ---

// The example demos' conversations, newest first, with the example and link label (staff only).
export async function getExampleConversations({ days = 7, slug = "", ref = "", limit = 100 } = {}) {
  const params = new URLSearchParams({ days: String(days), limit: String(limit) });
  if (slug) params.set("slug", slug);
  if (ref) params.set("ref", ref);
  const res = await apiRequest(`${FEEDBACK_BASE}/prospect/examples/conversations?${params}`, "GET", null, true);
  if (!res || res.error || res.cancel) {
    const err = new Error(res?.error || "EXAMPLE_CONVERSATIONS_FAILED");
    err.code = res?.error;
    throw err;
  }
  return res;
}

export async function getExampleUsage(days = 30) {
  const res = await apiRequest(`${FEEDBACK_BASE}/prospect/examples/usage?days=${encodeURIComponent(days)}`, "GET", null, true);
  if (!res || res.error || res.cancel) {
    const err = new Error(res?.error || "EXAMPLE_USAGE_FAILED");
    err.code = res?.error;
    throw err;
  }
  return res;
}
